/**
 * Exception messages Sentry should drop before they ever reach `beforeSend`.
 * These come from third-party extensions, injected scripts and browser globals
 * that contaminate our logs but say nothing about our own code.
 *
 * https://docs.sentry.io/clients/javascript/tips/#decluttering-sentry
 */
export const IGNORE_ERRORS = [
  // Random plugins/extensions
  "top.GLOBALS",
  // http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
  "originalCreateNotification",
  "canvas.contentDocument",
  "MyApp_RemoveAllHighlights",
  "http://tt.epicplay.com",
  "Can't find variable: ZiteReader",
  "jigsaw is not defined",
  "ComboSearch is not defined",
  "http://loading.retry.widdit.com/",
  "atomicFindClose",
  // Facebook borked
  "fb_xd_fragment",
  // ISP "optimizing" proxy. `Cache-Control: no-transform` seems to reduce this.
  // http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
  "bmi_SafeAddOnload",
  "EBCallBackMessageReceived",
  // http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
  "conduitPage",
  // Generic error code from errors outside the security sandbox
  "Script error.",
  // Avast extension
  "_avast_submit",
  // Third party making a duplicate variable declaration.
  // https://gist.github.com/pioug/b006c983538538066ea871d299d8e8bc
  "change_ua",
  // LaunchDarkly request failing behind uBlock.
  // https://github.com/launchdarkly/js-client-sdk/issues/255
  "A network error occurred.",
  // Safari on macOS only.
  // https://github.com/WordPress/openverse-frontend/issues/1632
  "Non-Error promise rejection captured with value: Not implemented on this platform",
  // On page load the refresh button becomes a cancel button; cancelling an
  // in-flight request there throws this in iOS Chrome/Safari.
  "TypeError: annulé",
  "TypeError: cancelled",
  // Safari extensions, 1Password being the likely culprit.
  "Error: Error response received for message <get-frame-manager-configuration>",
  // CefSharp Chromium bot interacting with the page.
  /^Non-Error promise rejection captured with value: Object Not Found Matching Id:\d+, MethodName:(update|simulateEvent), ParamCount:\d+$/,

  /**
   * Unactionable map library noise (mapbox-gl / maplibre-gl / Google Maps).
   * Deliberately narrow: every other map error still reports, tagged
   * `source=map-vendor`, so real problems stay visible.
   */
  // Tile/source fetch aborted mid pan, zoom or unmount. The map keeps working.
  // https://github.com/mapbox/mapbox-gl-js/issues/10498
  "AbortError",
  "The user aborted a request",
  "signal is aborted without reason",
  // WebGL context lost or context limit hit. Driven by the device GPU, browser,
  // or too many maps on one page. https://github.com/mapbox/mapbox-gl-js/issues/7332
  "Too many active WebGL contexts",
  /CONTEXT_LOST_WEBGL/i,
  /WebGL context (?:was )?lost/i,
  // Browser safety mechanism tripped by responsive containers. Benign.
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications",
];
